import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { callApi, HttpMethod } from "src/services/apiService";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

interface SystemPrompt {
  systemPromptId: number;
  promptType: string;
  persona: string;
  promptContent: string;
  isActive: boolean;
  createdOn: string;
  lastModifiedOn: string;
  version: number;
  description: string;
}

interface FormData {
  promptType: string;
  persona: string;
  promptContent: string;
  isActive: boolean;
  description: string;
}

const PROMPT_TYPES = ["Text", "Voice"];

const SystemPromptEditor: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = id === "new";

  const [formData, setFormData] = useState<FormData>({
    promptType: "Text",
    persona: "Default",
    promptContent: "",
    isActive: true,
    description: "",
  });
  const [originalPrompt, setOriginalPrompt] = useState<SystemPrompt | null>(
    null,
  );
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");

  // Load existing prompt data
  useEffect(() => {
    if (!isNew && id) {
      loadPrompt(parseInt(id));
    }
  }, [id, isNew]);

  const loadPrompt = async (promptId: number) => {
    setIsLoading(true);
    setError("");
    try {
      const response = await callApi(
        `sysadmin/ai/prompts/${promptId}`,
        HttpMethod.GET,
      );
      setOriginalPrompt(response);
      setFormData({
        promptType: response.promptType || "Text",
        persona: response.persona || "Default",
        promptContent: response.promptContent || "",
        isActive: response.isActive,
        description: response.description || "",
      });
    } catch (err: any) {
      console.error("Error loading prompt:", err);
      setError(`Error loading prompt: ${err.message || err}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    if (!formData.promptType.trim()) {
      setError("Prompt Type is required");
      return;
    }
    if (!formData.promptContent.trim()) {
      setError("Prompt Content is required");
      return;
    }

    setIsSaving(true);
    setError("");
    setSuccessMessage("");

    try {
      if (isNew) {
        const response = await callApi(
          "sysadmin/ai/prompts",
          HttpMethod.POST,
          formData,
        );
        setSuccessMessage("Prompt created successfully!");
        // Navigate to edit the newly created prompt
        setTimeout(() => {
          navigate(`/sysadmin/ai/prompts/${response.id}`);
        }, 1000);
      } else {
        const response = await callApi(
          `sysadmin/ai/prompts/${id}`,
          HttpMethod.PUT,
          formData,
        );
        setSuccessMessage(
          `Prompt updated successfully! Version: ${response.version}`,
        );
        // Reload to get updated version
        loadPrompt(parseInt(id!));
      }
    } catch (err: any) {
      console.error("Error saving prompt:", err);
      setError(`Error saving prompt: ${err.message || err}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    navigate("/sysadmin/ai/prompts");
  };

  const handleInputChange = (
    field: keyof FormData,
    value: string | boolean,
  ) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError("");
    setSuccessMessage("");
  };

  if (isLoading) {
    return (
      <div className="container-fluid p-3">
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "50vh" }}
        >
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-fluid p-3">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0">
          {isNew ? "Create New System Prompt" : `Edit System Prompt #${id}`}
        </h4>
        <button className="btn btn-secondary" onClick={handleCancel}>
          Back to List
        </button>
      </div>

      {error && (
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      )}

      {successMessage && (
        <div className="alert alert-success" role="alert">
          {successMessage}
        </div>
      )}

      {/* Metadata Card */}
      <div className="card mb-3">
        <div className="card-header">Prompt Settings</div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">
                Type <span className="text-danger">*</span>
              </label>
              <select
                className="form-select"
                value={formData.promptType}
                onChange={(e) =>
                  handleInputChange("promptType", e.target.value)
                }
              >
                {PROMPT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>
            <div className="col-md-3">
              <label className="form-label">Persona</label>
              <input
                type="text"
                className="form-control"
                value={formData.persona}
                onChange={(e) => handleInputChange("persona", e.target.value)}
                placeholder="e.g., Default, Donna, etc."
              />
            </div>
            <div className="col-md-4">
              <label className="form-label">Description</label>
              <input
                type="text"
                className="form-control"
                value={formData.description}
                onChange={(e) =>
                  handleInputChange("description", e.target.value)
                }
                placeholder="Brief description of this prompt..."
              />
            </div>
            <div className="col-md-2 d-flex align-items-end">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) =>
                    handleInputChange("isActive", e.target.checked)
                  }
                />
                <label className="form-check-label" htmlFor="isActive">
                  Active
                </label>
              </div>
            </div>
          </div>
          {originalPrompt && (
            <div className="row mt-3">
              <div className="col">
                <small className="text-muted">
                  Version: {originalPrompt.version} | Created:{" "}
                  {new Date(originalPrompt.createdOn).toLocaleString()} | Last
                  Modified:{" "}
                  {new Date(originalPrompt.lastModifiedOn).toLocaleString()}
                </small>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Split Editor/Preview */}
      <div className="card mb-3">
        <div className="card-header">
          Prompt Content <span className="text-danger">*</span>
        </div>
        <div className="card-body p-0">
          <div className="row g-0">
            {/* Editor Pane */}
            <div className="col-md-6 border-end">
              <div className="p-2 bg-light border-bottom">
                <small className="text-muted fw-bold">EDITOR (Markdown)</small>
              </div>
              <textarea
                className="form-control border-0 rounded-0"
                value={formData.promptContent}
                onChange={(e) =>
                  handleInputChange("promptContent", e.target.value)
                }
                style={{
                  height: "calc(100vh - 450px)",
                  minHeight: "400px",
                  resize: "none",
                  fontFamily: "Consolas, Monaco, 'Courier New', monospace",
                  fontSize: "14px",
                }}
                placeholder="Enter your system prompt content here...

You can use markdown formatting:
# Headers
- Lists
**Bold** and *italic*
`code`"
              />
            </div>

            {/* Preview Pane */}
            <div className="col-md-6">
              <div className="p-2 bg-light border-bottom">
                <small className="text-muted fw-bold">PREVIEW</small>
              </div>
              <div
                className="p-3 markdown-preview"
                style={{
                  height: "calc(100vh - 450px)",
                  minHeight: "400px",
                  overflowY: "auto",
                }}
              >
                {formData.promptContent ? (
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {formData.promptContent}
                  </ReactMarkdown>
                ) : (
                  <span className="text-muted">
                    Start typing to see preview...
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="d-flex justify-content-end gap-2">
        <button
          className="btn btn-secondary"
          onClick={handleCancel}
          disabled={isSaving}
        >
          Cancel
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={isSaving}
        >
          {isSaving ? (
            <>
              <span
                className="spinner-border spinner-border-sm me-2"
                role="status"
                aria-hidden="true"
              ></span>
              Saving...
            </>
          ) : isNew ? (
            "Create Prompt"
          ) : (
            "Save Changes"
          )}
        </button>
      </div>

      {/* Help Text */}
      <div className="mt-3">
        <small className="text-muted">
          The prompt content supports Markdown formatting. Use the preview pane
          to see how your prompt will look. Saving will increment the version
          number automatically.
        </small>
      </div>

      {/* Markdown Preview Styles */}
      <style>{`
        .markdown-preview h1 { font-size: 1.5rem; margin-bottom: 0.5rem; }
        .markdown-preview h2 { font-size: 1.3rem; margin-bottom: 0.5rem; }
        .markdown-preview h3 { font-size: 1.1rem; margin-bottom: 0.5rem; }
        .markdown-preview p { margin-bottom: 0.5rem; }
        .markdown-preview ul, .markdown-preview ol { margin-bottom: 0.5rem; padding-left: 1.5rem; }
        .markdown-preview code { background-color: #f4f4f4; padding: 0.2rem 0.4rem; border-radius: 3px; font-size: 0.9em; }
        .markdown-preview pre { background-color: #f4f4f4; padding: 1rem; border-radius: 5px; overflow-x: auto; }
        .markdown-preview pre code { padding: 0; background: none; }
        .markdown-preview blockquote { border-left: 4px solid #ddd; margin-left: 0; padding-left: 1rem; color: #666; }
        .markdown-preview table { border-collapse: collapse; width: 100%; margin-bottom: 0.5rem; }
        .markdown-preview th, .markdown-preview td { border: 1px solid #ddd; padding: 0.5rem; }
        .markdown-preview th { background-color: #f4f4f4; }
      `}</style>
    </div>
  );
};

export default SystemPromptEditor;
